<template>
  <div class="erp-nomenclatures-page">
    <card :title="$t('tools.erp_integration.nomenclatures')" :hide-title="true" id="erp-nomenclatures">
      <div class="nomenclatures-header">
        <h1 class="nomenclatures-header__title">{{ $t("tools.erp_integration.nomenclatures") }}</h1>
        <router-link
          to="/tools/erp-integration"
          class="btn btn-md btn-secondary nomenclatures-back-button"
          :aria-label="$t('common.back_to_list')"
          :title="$t('common.back_to_list')"
        >
          <span class="dx-icon dx-icon-arrowleft" aria-hidden="true"></span>
        </router-link>
      </div>

      <div v-if="entities.length === 0" class="placeholder-message">
        {{ $t("tools.erp_integration.no_entities_found") }}
      </div>

      <template v-else>
        <div class="nomenclature-tabs" role="tablist">
          <button
            v-for="entity in entities"
            :key="entity.code"
            type="button"
            class="nomenclature-tab-btn"
            :class="{ active: activeCode === entity.code }"
            role="tab"
            :aria-selected="activeCode === entity.code"
            @click="setActiveEntity(entity.code)"
          >
            {{ getEntityTabLabel(entity) }}
          </button>
        </div>

        <div class="tab-content pt-3 tab-content-container" v-if="activeEntity">
          <div v-if="showScreenLoader" class="tab-loading-overlay" aria-live="polite" aria-busy="true">
            <div class="tab-loading-spinner" />
            <div class="tab-loading-text">{{ $t("tools.erp_integration.schema_detect_loading") }}</div>
          </div>

          <div class="view-switcher" role="tablist">
            <button
              type="button"
              class="view-switcher__btn"
              :class="{ active: activeView === 'settings' }"
              role="tab"
              :aria-selected="activeView === 'settings'"
              @click="setActiveView('settings')"
            >
              {{ $t("tools.erp_integration.entity_settings") }}
            </button>
            <button
              type="button"
              class="view-switcher__btn"
              :class="{ active: activeView === 'records' }"
              role="tab"
              :aria-selected="activeView === 'records'"
              v-if="isEndpointMode"
              @click="setActiveView('records')"
            >
              {{ $t("tools.erp_integration.records_title") }}
            </button>
            <button
              type="button"
              class="view-switcher__btn"
              :class="{ active: activeView === 'mappings' }"
              role="tab"
              :aria-selected="activeView === 'mappings'"
              :disabled="!hasMappingSupport"
              @click="setActiveView('mappings')"
            >
              {{ $t("tools.erp_integration.mappings_title") }}
            </button>
            <button
              type="button"
              class="view-switcher__btn"
              :class="{ active: activeView === 'sync-logs' }"
              role="tab"
              :aria-selected="activeView === 'sync-logs'"
              @click="setActiveView('sync-logs')"
            >
              {{ $t("tools.erp_integration.sync_log_title") }}
            </button>
          </div>

          <div class="view-panel" v-if="activeView === 'settings'">
            <ErpEntitySettings
              :entity="activeEntity"
              :busy-save="savingSettings"
              :busy-detect="detectingSchema"
              :erp-field-options="erpFieldOptions"
              :sm-field-options="smFieldOptions"
              @save="saveEntitySettings"
              @detect-schema="detectSchema"
            />
          </div>

          <div class="view-panel" v-if="activeView === 'records'">
            <ErpEntityGrid
              ref="erpEntityGrid"
              :entity="activeEntity"
              :busy="syncingEntity"
              :cleaning="cleaningEntity"
              :show-clean-button="isEndpointMode"
              @refresh="refreshRecords"
              @sync="syncEntity"
              @clean="requestClean"
            />
          </div>

          <div class="view-panel" v-if="activeView === 'mappings'">
            <ErpEntityMappingsPanel
              :key="sourceCode"
              ref="erpMappingsPanel"
              :supported="hasMappingSupport"
              :entity-code="sourceCode"
              :data-source="mappingsDataSource"
              :key-field="mappingSourceKeyField"
              :mapping-by-source-key="mappingBySourceKey"
              :initial-cached-records="initialCachedRecords"
              :initial-cached-partner-records="initialCachedPartnerRecords"
              :initial-cached-consum-warehouse-records="initialCachedConsumWarehouseRecords"
              :erp-label="mappingValueLabel"
              :is-static-mode="!isEndpointMode"
              :season-options="parcelSeasonOptions"
              :selected-season-uuid="selectedParcelSeasonUuid"
              :require-season="sourceCode === 'PARCELS'"
              :mapping-status="mappingStatusFilter"
              :loading="loadingMappings"
              :busy-source-keys="busyMappingSourceKeys"
              :auto-match-busy="autoMatching"
              :auto-match-enabled="autoMatchEnabled"
              @refresh="reloadMappingsContext"
              @auto-match="runAutoMatch"
              @season-changed="onParcelSeasonChanged"
              @mapping-status-changed="onMappingStatusChanged"
              @save-mapping="saveLegacyMapping"
              @save-partner-mapping="savePartnerMapping"
              @save-consum-warehouse-mapping="saveConsumWarehouseMapping"
              @save-service-mapping="saveServiceMapping"
              @clear-mapping="clearLegacyMapping"
            />
          </div>

          <div class="view-panel" v-if="activeView === 'sync-logs'">
            <ErpSyncLogPanel
              ref="erpSyncLogPanel"
              :entity-code="sourceCode"
              :visible="activeView === 'sync-logs'"
            />
          </div>
        </div>
      </template>

      <div v-if="cleanConfirmVisible" class="clean-confirm-overlay" role="dialog" aria-modal="true">
        <div class="clean-confirm-dialog">
          <h4 class="clean-confirm-title">
            {{ $t("tools.erp_integration.clean_modal_title", { entity: activeEntity?.name || activeEntity?.code }) }}
          </h4>
          <div class="clean-confirm-summary">
            <div class="clean-confirm-row">
              <span>{{ $t("tools.erp_integration.clean_preview_active") }}:</span>
              <strong>{{ cleanPreview?.active_count || 0 }}</strong>
            </div>
            <div class="clean-confirm-row">
              <span>{{ $t("tools.erp_integration.clean_preview_trashed") }}:</span>
              <strong>{{ cleanPreview?.trashed_count || 0 }}</strong>
            </div>
            <div class="clean-confirm-row">
              <span>{{ $t("tools.erp_integration.clean_preview_mappings_linked") }}:</span>
              <strong>{{ cleanPreview?.mapping_links || 0 }}</strong>
            </div>
          </div>
          <p
            class="clean-confirm-body"
            v-html="$t('tools.erp_integration.clean_modal_body', { count: (cleanPreview?.active_count || 0) + (cleanPreview?.trashed_count || 0), entity: activeEntity?.name || activeEntity?.code })"
          />
          <div class="clean-confirm-actions">
            <button
              type="button"
              class="btn btn-sm btn-secondary"
              :disabled="cleaningEntity"
              @click="cancelClean"
            >
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-warning"
              :disabled="cleaningEntity || !cleanPreview?.can_apply"
              @click="confirmClean"
            >
              {{ cleaningEntity ? $t("common.please_wait") : $t("common.continue") }}
            </button>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import notify from "devextreme/ui/notify";
import { custom } from "devextreme/ui/dialog";
import CustomStore from "devextreme/data/custom_store";
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";
import ErpEntitySettings from "@/pages/tools/erp-integration/components/ErpEntitySettings";
import ErpEntityGrid from "@/pages/tools/erp-integration/components/ErpEntityGrid";
import ErpEntityMappingsPanel from "@/pages/tools/erp-integration/components/ErpEntityMappingsPanel";
import ErpSyncLogPanel from "@/pages/tools/erp-integration/components/ErpSyncLogPanel";
import {
  saveSyncLog,
  ERP_SYNC_LOG_STATUS,
} from "@/pages/tools/erp-integration/services/erpSyncLogService";
import { translateErpEntityLabel } from "@/pages/tools/erp-integration/services/erpEntityLabels";
import { formatServerErrorForLog } from "@/pages/tools/erp-integration/services/erpErrorDetails";

const SM_FIELD_OPTIONS = {
  PARTNERS: [
    "uuid",
    "name",
    "code",
    "id_number",
    "nr_reg",
    "intra_community_vat_code",
    "insured_code",
    "farmer_authorization_code",
    "statistical_code",
    "excise_authorization_no",
    "tax_warehouse_identification_code",
    "agricultural_cooperative_registration_no",
    "partner_type",
    "legal_form",
    "is_active",
  ],
};

function extractContainsSearch(filter) {
  if (!filter) return "";
  if (Array.isArray(filter) && filter.length === 3 && typeof filter[2] === "string") {
    return filter[2];
  }
  if (Array.isArray(filter)) {
    for (const item of filter) {
      if (Array.isArray(item)) {
        const found = extractContainsSearch(item);
        if (found) return found;
      }
    }
  }
  return "";
}

export default {
  name: "ErpNomenclaturesPage",
  components: {
    ErpEntitySettings,
    ErpEntityGrid,
    ErpEntityMappingsPanel,
    ErpSyncLogPanel,
  },
  data() {
    return {
      activeCode: null,
      syncingEntity: false,
      savingSettings: false,
      detectingSchema: false,
      loadingMappings: false,
      busyMappingSourceKeys: {},
      autoMatching: false,
      bootstrapping: false,
      switchingTab: false,
      switchingView: false,
      legacyMappings: [],
      initialCachedRecords: [],
      initialCachedPartnerRecords: [],
      initialCachedConsumWarehouseRecords: [],
      mappingContextVersion: 0,
      mappingLoadVersion: 0,
      activeView: "settings",
      cleaningEntity: false,
      cleanPreview: null,
      cleanConfirmVisible: false,
      parcelSeasonOptions: [],
      selectedParcelSeasonUuid: "",
      mappingStatusFilter: "all",
    };
  },
  computed: {
    ...mapGetters({
      entities: "erpIntegration/erpEntities",
      recordsByCode: "erpIntegration/erpEntityRecords",
      seasons: "seasons/seasons",
    }),
    sourceCode() {
      return (this.activeEntity?.code || "").toUpperCase();
    },
    hasLegacyMappings() {
      return ["MEASURE_UNITS", "PRODUCT_TYPES", "WAREHOUSES", "LEGAL_ENTITIES", "PARCELS"].includes(
        this.sourceCode
      );
    },
    hasGenericStaticMappings() {
      return ["WORK_TYPES", "SEASONS", "CULTURES", "PARTNERS", "PRODUCTS"].includes(this.sourceCode);
    },
    hasMappingSupport() {
      if (this.hasLegacyMappings) {
        return true;
      }

      return this.hasGenericStaticMappings;
    },
    usesGenericStaticMappings() {
      return !this.hasLegacyMappings && this.hasGenericStaticMappings;
    },
    sourceConfig() {
      const map = {
        MEASURE_UNITS: { keyField: "id" },
        PRODUCT_TYPES: { keyField: "uuid" },
        WAREHOUSES: { keyField: "uuid" },
        LEGAL_ENTITIES: { keyField: "uuid" },
        PARCELS: { keyField: "uuid" },
        WORK_TYPES: { keyField: "uuid" },
        SEASONS: { keyField: "uuid" },
        CULTURES: { keyField: "uuid" },
        PARTNERS: { keyField: "uuid" },
        PRODUCTS: { keyField: "uuid" },
      };

      return map[this.sourceCode] || { keyField: "uuid" };
    },
    sourceNameField() {
      const map = {
        MEASURE_UNITS: "name",
        PRODUCT_TYPES: "name",
        WAREHOUSES: "name",
        LEGAL_ENTITIES: "department_name",
        PARCELS: "name",
        WORK_TYPES: "name",
        SEASONS: "name",
        CULTURES: "name",
        PARTNERS: "name",
        PRODUCTS: "name",
      };

      return map[this.sourceCode] || "name";
    },
    sourceCodeField() {
      const map = {
        MEASURE_UNITS: "code",
        PRODUCT_TYPES: "code",
        WAREHOUSES: "code",
        LEGAL_ENTITIES: "department_name",
        PARCELS: "code",
        WORK_TYPES: "code",
        SEASONS: "code",
        CULTURES: "code",
        PARTNERS: "code",
        PRODUCTS: "code",
      };

      return map[this.sourceCode] || "code";
    },
    activeEntity() {
      if (!this.activeCode) {
        return this.entities[0] || null;
      }

      return this.entities.find((item) => item?.code === this.activeCode) || this.entities[0] || null;
    },
    activeEntityRecords() {
      if (!this.activeEntity?.code) {
        return [];
      }

      return this.recordsByCode(this.activeEntity.code);
    },
    isEndpointMode() {
      const mode = (this.activeEntity?.data_source_type || "ENDPOINT").toUpperCase();
      return mode === "ENDPOINT";
    },
    mappingSourceKeyField() {
      return this.sourceConfig.keyField || "uuid";
    },
    mappingsDataSource() {
      const code = this.sourceCode;
      if (!code || !this.hasMappingSupport) {
        return [];
      }

      const keyField = this.sourceConfig.keyField;
      const mappingStatus = this.mappingStatusFilter;
      const contextVersion = this.mappingContextVersion;
      const seasonUuid = code === "PARCELS" ? this.selectedParcelSeasonUuid : "";
      const url = endpoints.replaceParams(endpoints.ERP_ENTITY_SOURCE_ROWS, { entityCode: code });

      return new CustomStore({
        key: keyField,
        load: async (loadOptions) => {
          const isContextLoad = !loadOptions.group;
          const loadId = isContextLoad ? ++this.mappingLoadVersion : null;
          const isCurrent = () => isContextLoad && code === this.sourceCode
            && contextVersion === this.mappingContextVersion
            && loadId === this.mappingLoadVersion;
          if (code === "PARCELS" && !seasonUuid) {
            if (isContextLoad) this.loadingMappings = false;
            return { data: [], totalCount: 0 };
          }

          const limit = loadOptions.take || 25;
          const skip = loadOptions.skip ?? 0;
          const page = Math.floor(skip / limit) + 1;
          const search = extractContainsSearch(loadOptions.filter);
          const params = { page, limit, include_mappings: 1 };

          if (search.trim() !== "") {
            params.search = search.trim();
          }

          if (mappingStatus && mappingStatus !== "all") {
            params.mapping_status = mappingStatus;
          }

          if (code === "PARCELS") {
            params.season_uuid = seasonUuid;
          }

          if (isContextLoad) this.loadingMappings = true;
          try {
            const response = await axios.get(url, { params });
            const inner = response?.data?.data;
            if (!Array.isArray(inner?.data) || !Array.isArray(inner?.mappings)
              || !Array.isArray(inner?.records) || !Array.isArray(inner?.partner_records)
              || (inner.consum_warehouse_records !== undefined && !Array.isArray(inner.consum_warehouse_records))) {
              throw new Error("Invalid ERP source mapping context response");
            }

            if (isCurrent()) {
              this.legacyMappings = inner.mappings;
              this.initialCachedRecords = inner.records;
              this.initialCachedPartnerRecords = inner.partner_records;
              this.initialCachedConsumWarehouseRecords = inner.consum_warehouse_records || [];
              await this.$nextTick();
            }

            const data = inner.data.map((row) => ({
              ...row,
              display_name: row?.name || row?.department_name || row?.Denumire || row?.Nume || row?.Name || row?.code || "",
              department_name: row?.department?.name || row?.department_name || "",
            }));

            return { data, totalCount: inner.pagination?.total ?? inner.meta?.total ?? data.length };
          } catch (error) {
            if (isCurrent()) {
              this.showActionError(error, "mappings_load_error");
            }
            throw error;
          } finally {
            if (isCurrent()) {
              this.loadingMappings = false;
            }
          }
        },
      });
    },
    mappingBySourceKey() {
      return this.legacyMappings.reduce((accumulator, item) => {
        const sourceKey = this.getSourceKeyFromMapping(item);

        if (!sourceKey) {
          return accumulator;
        }

        const staticValue = (item?.static_value || "").toString();
        const erpRecordId = item?.erp_entity_record_id
          ? String(item.erp_entity_record_id)
          : (this.usesGenericStaticMappings ? staticValue : "");

        accumulator[sourceKey] = {
          id: item.id,
          erpRecordId,
          erpPartnerRecordId: item?.erp_partner_record_id ? String(item.erp_partner_record_id) : "",
          erpConsumWarehouseRecordId: item?.erp_consum_warehouse_record_id ? String(item.erp_consum_warehouse_record_id) : "",
          staticValue,
          serviceStaticValue: (item?.service_static_value || "").toString(),
        };

        return accumulator;
      }, {});
    },
    erpRecordOptions() {
      return this.activeEntityRecords.map((record) => ({
        value: String(record.id),
        label: `${record.external_key || record.id} - ${this.getErpRecordDisplayName(record)}`,
      }));
    },
    mappingValueLabel() {
      return this.isEndpointMode
        ? this.$t("tools.erp_integration.erp_entry_label")
        : this.$t("tools.erp_integration.static_value_label");
    },
    erpFieldOptions() {
      const schema = Array.isArray(this.activeEntity?.schema_definition)
        ? this.activeEntity.schema_definition
        : [];

      return schema.map((item) => item?.name).filter(Boolean);
    },
    smFieldOptions() {
      return SM_FIELD_OPTIONS[this.sourceCode] || [];
    },
    autoMatchEnabled() {
      const rules = Array.isArray(this.activeEntity?.auto_match_rules)
        ? this.activeEntity.auto_match_rules
        : [];

      return rules.some((item) => item?.erp_field && item?.sm_field);
    },
    showScreenLoader() {
      return this.bootstrapping || this.switchingTab || this.switchingView || this.detectingSchema;
    },
  },
  methods: {
    ...mapActions({
      fetchErpEntities: "erpIntegration/fetchErpEntities",
      fetchErpEntityRecords: "erpIntegration/fetchErpEntityRecords",
      updateErpEntity: "erpIntegration/updateErpEntity",
      detectErpEntitySchema: "erpIntegration/detectErpEntitySchema",
      syncErpEntity: "erpIntegration/syncErpEntity",
      fetchErpSyncLog: "erpIntegration/fetchErpSyncLog",
      applyErpEntityAutoMatch: "erpIntegration/applyErpEntityAutoMatch",
      storeErpMeasureUnitMapping: "measureUnits/storeErpMeasureUnitMapping",
      updateErpMeasureUnitMapping: "measureUnits/updateErpMeasureUnitMapping",
      destroyErpMeasureUnitMapping: "measureUnits/destroyErpMeasureUnitMapping",
      storeErpProductTypeMapping: "smProductTypes/storeErpProductTypeMapping",
      updateErpProductTypeMapping: "smProductTypes/updateErpProductTypeMapping",
      destroyErpProductTypeMapping: "smProductTypes/destroyErpProductTypeMapping",
      storeErpWarehouseMapping: "smWarehouses/storeErpWarehouseMapping",
      updateErpWarehouseMapping: "smWarehouses/updateErpWarehouseMapping",
      destroyErpWarehouseMapping: "smWarehouses/destroyErpWarehouseMapping",
      storeErpLegalEntityMapping: "company/storeErpLegalEntityMapping",
      updateErpLegalEntityMapping: "company/updateErpLegalEntityMapping",
      destroyErpLegalEntityMapping: "company/destroyErpLegalEntityMapping",
      storeErpEntityStaticMapping: "erpIntegration/storeErpEntityStaticMapping",
      destroyErpEntityStaticMapping: "erpIntegration/destroyErpEntityStaticMapping",
      storeErpParcelMapping: "erpIntegration/storeErpParcelMapping",
      updateErpParcelMapping: "erpIntegration/updateErpParcelMapping",
      destroyErpParcelMapping: "erpIntegration/destroyErpParcelMapping",
      previewErpEntityClean: "erpIntegration/previewErpEntityClean",
      applyErpEntityClean: "erpIntegration/applyErpEntityClean",
      getSeasons: "seasons/getSeasons",
    }),
    normalizeSeasonOptions(items) {
      return (Array.isArray(items) ? items : [])
        .map((season) => ({
          value: season?.uuid,
          label: season?.name,
          year: Number(season?.year || 0),
        }))
        .filter((season) => season.value);
    },
    selectDefaultParcelSeason() {
      if (this.selectedParcelSeasonUuid) {
        const selectedStillExists = this.parcelSeasonOptions.some(
          (season) => season.value === this.selectedParcelSeasonUuid
        );

        if (selectedStillExists) {
          return;
        }
      }

      const latestSeason = [...this.parcelSeasonOptions]
        .sort((a, b) => b.year - a.year)[0];

      this.selectedParcelSeasonUuid = latestSeason?.value || "";
    },
    async loadParcelSeasons() {
      try {
        const response = await this.getSeasons({ limit: 1000 });
        const seasonRows = Array.isArray(response?.data) ? response.data : this.seasons;
        this.parcelSeasonOptions = this.normalizeSeasonOptions(seasonRows);
        this.selectDefaultParcelSeason();

      } catch (e) {
        this.showActionError(e, "parcel_seasons_load_error");
      }
    },
    onParcelSeasonChanged(value) {
      this.selectedParcelSeasonUuid = value || "";
    },
    onMappingStatusChanged(value) {
      this.mappingStatusFilter = value || "all";
    },
    refreshMappingsGridIfFiltered() {
      if (this.mappingStatusFilter === "all") {
        return;
      }
      this.$nextTick(() => {
        this.$refs.erpMappingsPanel?.reload();
      });
    },
    extractErrorMessage(error) {
      const detail = this.extractBackendErrorDetail(error?.response?.data);

      return (
        detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        ""
      );
    },
    extractBackendErrorDetail(data) {
      const errorBag = data?.errors?.error;
      const entries = Array.isArray(errorBag) ? errorBag : errorBag ? [errorBag] : [];

      return entries
        .flat()
        .filter(Boolean)
        .join(" | ");
    },
    showActionError(error, fallbackKey) {
      const message = this.extractErrorMessage(error);
      notify(message || this.$t(`tools.erp_integration.${fallbackKey}`), "error", 3000);
    },
    async setActiveEntity(code) {
      if (code === this.activeCode) {
        return;
      }
      this.activeCode = code;
      this.resetMappingContext();
      this.mappingStatusFilter = "all";
      if (!this.hasMappingSupport && this.activeView === "mappings") {
        this.activeView = "settings";
      }

      if (!this.isEndpointMode && this.activeView === "records") {
        this.activeView = this.hasMappingSupport ? "mappings" : "settings";
      }

      this.switchingTab = true;

      try {
        if (this.activeView === "mappings") {
          if (this.sourceCode === "PARCELS" && this.parcelSeasonOptions.length === 0) {
            await this.loadParcelSeasons();
          }
        }
      } finally {
        this.switchingTab = false;
      }
    },
    getEntityTabLabel(entity) {
      return translateErpEntityLabel(
        this.$t.bind(this),
        entity?.code,
        entity?.name || entity?.code || ""
      );
    },
    async setActiveView(view) {
      if (view === this.activeView) {
        return;
      }
      if (view === "mappings" && !this.hasMappingSupport) {
        return;
      }

      if (view === "records" && !this.isEndpointMode) {
        this.activeView = this.hasMappingSupport ? "mappings" : "settings";
        return;
      }

      this.activeView = view;

      if (view !== "records" && view !== "mappings") {
        return;
      }

      this.switchingView = true;

      try {
        if (view === "mappings") {
          this.resetMappingContext();
          if (this.sourceCode === "PARCELS" && this.parcelSeasonOptions.length === 0) {
            await this.loadParcelSeasons();
          }
        }
        // records view: a CustomStore automatikusan tölt
      } finally {
        this.switchingView = false;
      }
    },
    async bootstrap() {
      this.bootstrapping = true;

      try {
        const entities = await this.fetchErpEntities();
        if (entities.length > 0) {
          const queryEntity = this.$route?.query?.entity;
          const target = queryEntity ? entities.find((e) => e.code === queryEntity) : null;
          this.activeCode = target ? target.code : entities[0].code;

          const queryView = this.$route?.query?.view;
          if (queryView && ["settings", "records", "mappings", "sync-logs"].includes(queryView)) {
            this.activeView = queryView;
          }

          if (this.activeView === "mappings") {
            this.resetMappingContext();
            if (this.sourceCode === "PARCELS") {
              await this.loadParcelSeasons();
            }
          }
        }
      } catch (e) {
        this.showActionError(e, "entities_load_error");
      } finally {
        this.bootstrapping = false;
      }
    },
    async refreshRecords() {
      if (!this.activeEntity?.code || !this.isEndpointMode) {
        return;
      }

      this.$refs.erpEntityGrid?.reload();
    },
    resetMappingContext() {
      this.mappingContextVersion++;
      this.legacyMappings = [];
      this.initialCachedRecords = [];
      this.initialCachedPartnerRecords = [];
      this.initialCachedConsumWarehouseRecords = [];
      this.loadingMappings = false;
    },
    async reloadMappingsContext() {
      await this.$refs.erpMappingsPanel?.reload();
    },
    getSourceKeyFromMapping(item, code = this.sourceCode) {
      const fields = {
        MEASURE_UNITS: "cmn_measure_unit_id",
        PRODUCT_TYPES: "sm_product_type_uuid",
        WAREHOUSES: "sm_warehouse_uuid",
        LEGAL_ENTITIES: "sm_department_uuid",
        PARCELS: "sm_parcel_uuid",
      };
      const value = item?.[fields[code] || "sm_source_key"];
      return value === null || typeof value === "undefined" ? "" : String(value);
    },
    getSourceKeyFromRow(sourceRow) {
      if (!sourceRow) {
        return "";
      }

      if (this.sourceConfig.keyField === "id") {
        return typeof sourceRow.id === "undefined" || sourceRow.id === null ? "" : String(sourceRow.id);
      }

      return sourceRow.uuid || "";
    },
    getSourceCodeFromRow(sourceRow) {
      if (!sourceRow) {
        return "";
      }

      const value = sourceRow?.[this.sourceCodeField] || sourceRow?.code || sourceRow?.Cod || sourceRow?.id;
      return value === null || typeof value === "undefined" ? "" : String(value);
    },
    getSourceNameFromRow(sourceRow) {
      if (!sourceRow) {
        return "";
      }

      const value = sourceRow?.[this.sourceNameField] || sourceRow?.name || sourceRow?.department_name || sourceRow?.code;
      return value === null || typeof value === "undefined" ? "" : String(value);
    },
    isScalarValue(value) {
      return ["string", "number", "boolean"].includes(typeof value) || value === null;
    },
    getErpRecordDisplayName(record) {
      const raw = record?.raw_data || {};
      return (
        raw.Denumire ||
        raw.DENUMIRE ||
        raw.denumire ||
        raw.Name ||
        raw.name ||
        raw.NAME ||
        raw.Nume ||
        raw.NUME ||
        raw.nume ||
        raw.DenumirePartener ||
        raw.DENUMIRE_PARTENER ||
        record.external_key ||
        record.id
      );
    },
    buildMappingPayload(sourceRow, erpRecordId) {
      const payload = this.isEndpointMode
        ? { erp_entity_record_id: erpRecordId ? Number(erpRecordId) : null }
        : { static_value: (erpRecordId || "").toString().trim() || null };

      if (this.activeEntity?.code === "MEASURE_UNITS") {
        payload.cmn_measure_unit_id = Number(sourceRow.id);
      } else if (this.activeEntity?.code === "PRODUCT_TYPES") {
        payload.sm_product_type_uuid = sourceRow.uuid;
      } else if (this.activeEntity?.code === "WAREHOUSES") {
        payload.sm_warehouse_uuid = sourceRow.uuid;
      } else if (this.activeEntity?.code === "LEGAL_ENTITIES") {
        payload.sm_department_uuid = sourceRow.uuid;
      } else if (this.activeEntity?.code === "PARCELS") {
        payload.sm_parcel_uuid = sourceRow.uuid;
      }

      return payload;
    },
    async saveLegacyMapping({ sourceRow, value }) {
      if (this.sourceCode === "LEGAL_ENTITIES") {
        return this.saveLegalEntityMapping({ sourceRow, value, field: "subunit" });
      }
      if (this.sourceCode === "PRODUCTS") {
        return this.saveProductMappingField({ sourceRow, value, field: "static_value" });
      }
      const sourceKey = this.getSourceKeyFromRow(sourceRow);
      const existing = this.mappingBySourceKey[sourceKey];

      const normalizedValue = value === null || typeof value === "undefined" ? "" : String(value).trim();
      const currentValue = this.isEndpointMode ? existing?.erpRecordId : existing?.staticValue;
      if (normalizedValue === (currentValue || "")) {
        return;
      }
      if (!normalizedValue) {
        return this.clearLegacyMapping({ sourceRow });
      }
      const code = this.sourceCode;
      return this.persistMappingChange({ sourceRow, write: async () => {
        if (this.usesGenericStaticMappings) {
          return this.storeErpEntityStaticMapping({
            code,
            payload: {
              sm_source_key: sourceKey,
              sm_source_name: this.getSourceNameFromRow(sourceRow) || this.getSourceCodeFromRow(sourceRow),
              static_value: normalizedValue,
            },
          });
        }
        const payload = this.buildMappingPayload(sourceRow, normalizedValue);
        if (code === "PARCELS") {
          return existing?.id
            ? this.updateErpParcelMapping({ code, id: existing.id, payload })
            : this.storeErpParcelMapping({ code, payload });
        }
        const actions = this.getLegacyMappingActions(code);
        const response = existing?.id
          ? await actions.update({ id: existing.id, ...payload })
          : await actions.store(payload);
        return response?.data;
      } });
    },
    async saveServiceMapping({ sourceRow, value }) {
      return this.saveProductMappingField({ sourceRow, value, field: "service_static_value" });
    },
    async saveProductMappingField({ sourceRow, value, field }) {
      const sourceKey = this.getSourceKeyFromRow(sourceRow);
      const normalizedValue = value === null || typeof value === "undefined" ? null : String(value).trim() || null;
      const existing = this.mappingBySourceKey[sourceKey];
      const currentValue = field === "static_value" ? existing?.staticValue : existing?.serviceStaticValue;
      if ((currentValue || null) === normalizedValue) {
        return;
      }
      const otherValue = field === "static_value" ? existing?.serviceStaticValue : existing?.staticValue;
      return this.persistMappingChange({
        sourceRow,
        allowRemoval: normalizedValue === null && !otherValue,
        write: () => this.storeErpEntityStaticMapping({
          code: "PRODUCTS",
          payload: {
            sm_source_key: sourceKey,
            sm_source_name: this.getSourceNameFromRow(sourceRow) || this.getSourceCodeFromRow(sourceRow),
            [field]: normalizedValue,
          },
        }),
      });
    },
    async savePartnerMapping({ sourceRow, value }) {
      return this.saveLegalEntityMapping({ sourceRow, value, field: "partner" });
    },
    async saveConsumWarehouseMapping({ sourceRow, value }) {
      return this.saveLegalEntityMapping({ sourceRow, value, field: "consumWarehouse" });
    },
    async saveLegalEntityMapping({ sourceRow, value, field, remove = false }) {
      const sourceKey = this.getSourceKeyFromRow(sourceRow);
      if (this.sourceCode !== "LEGAL_ENTITIES") {
        return;
      }

      const existing = this.mappingBySourceKey[sourceKey];
      const normalizedValue = value === null || typeof value === "undefined" ? "" : String(value).trim();
      const recordFields = {
        partner: ["erpPartnerRecordId", "erp_partner_record_id"],
        consumWarehouse: ["erpConsumWarehouseRecordId", "erp_consum_warehouse_record_id"],
      };
      const recordField = recordFields[field];
      const currentValue = recordField
        ? existing?.[recordField[0]]
        : (this.isEndpointMode ? existing?.erpRecordId : existing?.staticValue);
      if (remove ? !existing?.id : normalizedValue === (currentValue || "")) {
        return;
      }

      const hasOtherMapping = (field !== "subunit" && (existing?.erpRecordId || existing?.staticValue))
        || (field !== "partner" && existing?.erpPartnerRecordId)
        || (field !== "consumWarehouse" && existing?.erpConsumWarehouseRecordId);
      const deleteMapping = remove || (!normalizedValue && !hasOtherMapping);
      return this.persistMappingChange({ sourceRow, allowRemoval: deleteMapping, write: async () => {
        if (deleteMapping) {
          await this.destroyErpLegalEntityMapping(existing.id);
          return null;
        } else {
          const payload = recordField
            ? { [recordField[1]]: normalizedValue ? Number(normalizedValue) : null }
            : (normalizedValue ? this.buildMappingPayload(sourceRow, normalizedValue) : {
              erp_entity_record_id: null,
              wm_legal_entity_id: null,
              static_value: null,
            });
          const response = existing?.id
            ? await this.updateErpLegalEntityMapping({ id: existing.id, ...payload })
            : await this.storeErpLegalEntityMapping({ sm_department_uuid: sourceKey, ...payload });
          return response?.data;
        }
      } });
    },
    getLegacyMappingActions(code) {
      const actions = {
        MEASURE_UNITS: {
          store: this.storeErpMeasureUnitMapping,
          update: this.updateErpMeasureUnitMapping,
          destroy: this.destroyErpMeasureUnitMapping,
        },
        PRODUCT_TYPES: {
          store: this.storeErpProductTypeMapping,
          update: this.updateErpProductTypeMapping,
          destroy: this.destroyErpProductTypeMapping,
        },
        WAREHOUSES: {
          store: this.storeErpWarehouseMapping,
          update: this.updateErpWarehouseMapping,
          destroy: this.destroyErpWarehouseMapping,
        },
      };
      if (!actions[code]) {
        throw new Error(`Unsupported legacy mapping entity: ${code}`);
      }
      return actions[code];
    },
    async persistMappingChange({ sourceRow, write, allowRemoval = false }) {
      const code = this.sourceCode;
      const sourceKey = this.getSourceKeyFromRow(sourceRow);
      if (this.busyMappingSourceKeys[sourceKey]) {
        return;
      }
      const mappings = this.legacyMappings;
      this.$set(this.busyMappingSourceKeys, sourceKey, true);
      try {
        const savedMapping = await write();
        if (!(allowRemoval && savedMapping === null) &&
            (!savedMapping?.id || this.getSourceKeyFromMapping(savedMapping, code) !== sourceKey)) {
          throw new Error(`Invalid ${code} mapping response`);
        }
        // A tab switch or refresh may have loaded a different mapping context.
        if (this.sourceCode === code && this.legacyMappings === mappings) {
          const index = mappings.findIndex((mapping) => this.getSourceKeyFromMapping(mapping, code) === sourceKey);
          if (savedMapping) {
            if (index >= 0) {
              mappings.splice(index, 1, savedMapping);
            } else {
              mappings.push(savedMapping);
            }
          } else if (index >= 0) {
            mappings.splice(index, 1);
          }
        }
      } catch (e) {
        this.showActionError(e, "mapping_save_error");
        if (this.sourceCode === code && this.legacyMappings === mappings) {
          this.$refs.erpMappingsPanel?.repaintMappingRow(sourceKey);
        }
      } finally {
        this.$delete(this.busyMappingSourceKeys, sourceKey);
      }
    },
    async clearLegacyMapping({ sourceRow }) {
      if (this.sourceCode === "LEGAL_ENTITIES") {
        return this.saveLegalEntityMapping({ sourceRow, remove: true });
      }
      if (this.sourceCode === "PRODUCTS") {
        return this.saveProductMappingField({ sourceRow, value: null, field: "static_value" });
      }
      const sourceKey = this.getSourceKeyFromRow(sourceRow);
      const existing = this.mappingBySourceKey[sourceKey];

      if (!existing?.id) {
        return;
      }

      const code = this.sourceCode;
      return this.persistMappingChange({ sourceRow, allowRemoval: true, write: async () => {
        if (this.usesGenericStaticMappings) {
          await this.destroyErpEntityStaticMapping({ code, id: existing.id });
        } else if (code === "PARCELS") {
          await this.destroyErpParcelMapping({ code, id: existing.id });
        } else {
          await this.getLegacyMappingActions(code).destroy(existing.id);
        }
        return null;
      } });
    },
    async runAutoMatch() {
      if (!this.hasMappingSupport || !this.activeEntity?.code) {
        return;
      }

      const payload = {};
      if (this.sourceCode === "PARCELS") {
        if (!this.selectedParcelSeasonUuid) {
          notify(this.$t("tools.erp_integration.parcel_mapping_select_season_hint"), "warning", 2500);
          return;
        }
        payload.season_uuid = this.selectedParcelSeasonUuid;
      }

      const rules = (Array.isArray(this.activeEntity?.auto_match_rules) ? this.activeEntity.auto_match_rules : [])
        .filter((item) => item?.erp_field && item?.sm_field);

      if (rules.length === 0) {
        notify(this.$t("tools.erp_integration.auto_match_no_rules"), "warning", 2500);
        return;
      }

      this.autoMatching = true;

      try {
        const result = await this.applyErpEntityAutoMatch({
          code: this.activeEntity.code,
          payload,
        });

        await this.reloadMappingsContext();

        const content = [
          this.$t("tools.erp_integration.auto_match_result_matched", { count: result?.matched_count || 0 }),
          this.$t("tools.erp_integration.auto_match_result_skipped", { count: result?.skipped_no_match_count || 0 }),
          this.$t("tools.erp_integration.auto_match_result_existing", { count: result?.skipped_existing_count || 0 }),
          this.$t("tools.erp_integration.auto_match_result_ambiguous", { count: result?.ambiguous_count || 0 }),
        ].join("<br>");

        custom({
          title: this.$t("tools.erp_integration.auto_match_result_title"),
          messageHtml: content,
        }).show();
      } catch (e) {
        this.showActionError(e, "auto_match_error");
      } finally {
        this.autoMatching = false;
      }
    },
    async saveEntitySettings(payload) {
      if (!this.activeEntity?.code) {
        return;
      }

      this.savingSettings = true;

      try {
        await this.updateErpEntity({
          code: this.activeEntity.code,
          payload,
        });

        notify(this.$t("tools.erp_integration.entity_settings_saved"), "success", 1800);
      } catch (e) {
        this.showActionError(e, "endpoint_save_error");
      } finally {
        this.savingSettings = false;
      }
    },
    async detectSchema() {
      if (!this.activeEntity?.code) {
        return;
      }

      this.detectingSchema = true;

      try {
        await this.detectErpEntitySchema(this.activeEntity.code);
        notify(this.$t("tools.erp_integration.schema_detect_success"), "success", 1800);
      } catch (e) {
        this.showActionError(e, "schema_detect_error");
      } finally {
        this.detectingSchema = false;
      }
    },
    async syncEntity() {
      if (!this.activeEntity?.code) {
        return;
      }

      this.syncingEntity = true;
      const startedAt = Date.now();
      const entityCode = this.activeEntity.code;
      const entityName = this.activeEntity?.name || entityCode;

      try {
        const initialResponse = await this.syncErpEntity(entityCode);
        let result = initialResponse;

        // If the sync was dispatched as an asynchronous queue job, poll until completion
        if (initialResponse?.queued && initialResponse?.status === "running" && initialResponse?.sync_log_uuid) {
          notify(
            this.$t("tools.erp_integration.sync_in_progress"),
            "info",
            3000
          );

          result = await this.pollSyncLog(initialResponse.sync_log_uuid);
        }

        this.$refs.erpEntityGrid?.reload();
        await this.reloadMappingsContext();

        this.writeSyncLog({
          entityCode,
          entityName,
          status: ERP_SYNC_LOG_STATUS.SUCCESS,
          stats: result,
          durationMs: Date.now() - startedAt,
        });

        const lines = [
          this.$t("tools.erp_integration.sync_summary_fetched", { count: result?.fetched_count || 0 }),
          this.$t("tools.erp_integration.sync_summary_processed", { count: result?.processed_count || 0 }),
          this.$t("tools.erp_integration.sync_summary_skipped", { count: result?.skipped_count || 0 }),
          this.$t("tools.erp_integration.sync_summary_inserted", { count: result?.inserted_count || 0 }),
          this.$t("tools.erp_integration.sync_summary_updated", { count: result?.updated_count || 0 }),
          this.$t("tools.erp_integration.sync_summary_untouched", { count: result?.untouched_count || 0 }),
        ];
        const content = lines.join("<br>");

        custom({
          title: this.$t("tools.erp_integration.sync_summary_title", {
            tab: entityName,
          }),
          messageHtml: content,
        }).show();
      } catch (e) {
        const { text, details } = formatServerErrorForLog(
          e,
          this.$t("tools.erp_integration.sync_error")
        );

        this.writeSyncLog({
          entityCode,
          entityName,
          status: ERP_SYNC_LOG_STATUS.ERROR,
          errorMessage: text,
          errorDetails: details,
          durationMs: Date.now() - startedAt,
        });

        if (e?.response?.status === 503) {
          notify(this.$t("tools.erp_integration.sync_no_worker"), "error", 4000);
        } else {
          this.showActionError(e, "sync_error");
        }
      } finally {
        this.syncingEntity = false;
      }
    },
    async pollSyncLog(uuid) {
      const pollIntervalMs = 2500;
      const maxAttempts = 360; // 15 minutes max
      let attempts = 0;
      let lastReportedCount = 0;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        attempts++;

        try {
          const log = await this.fetchErpSyncLog(uuid);

          if (!log) {
            continue;
          }

          if (log.status === "success") {
            return log;
          }

          if (log.status === "error") {
            const errorMsg = log.error_message || this.$t("tools.erp_integration.sync_error");
            throw new Error(errorMsg);
          }

          if (log.status === "oprit") {
            throw new Error(this.$t("tools.erp_integration.sync_cancelled_by_user"));
          }

          if (log.status === "running" && log.fetched_count > 0 && log.fetched_count !== lastReportedCount) {
            lastReportedCount = log.fetched_count;
            notify(
              this.$t("tools.erp_integration.sync_progress_status", { count: log.fetched_count }),
              "info",
              2000
            );
          }
        } catch (err) {
          // If this is a terminal sync failure thrown from above, re-raise it
          if (err?.message && !err?.response) {
            throw err;
          }
          console.warn("ERP sync log polling notice:", err);
        }
      }

      throw new Error(
        this.$t("tools.erp_integration.sync_timeout_warning")
      );
    },
    writeSyncLog(entry) {
      // A naplózás soha nem befolyásolhatja a szinkron folyamatot.
      Promise.resolve(saveSyncLog(entry))
        .then(() => {
          if (this.activeView === "sync-logs") {
            this.$refs.erpSyncLogPanel?.reload();
          }
        })
        .catch(() => {});
    },
    async requestClean() {
      if (!this.activeEntity?.code) {
        return;
      }

      this.cleaningEntity = true;
      this.cleanPreview = null;

      try {
        const preview = await this.previewErpEntityClean({
          code: this.activeEntity.code,
        });
        this.cleanPreview = preview;
        this.cleanConfirmVisible = true;
      } catch (e) {
        this.showActionError(e, "clean_error");
      } finally {
        this.cleaningEntity = false;
      }
    },
    cancelClean() {
      this.cleanConfirmVisible = false;
      this.cleanPreview = null;
    },
    async confirmClean() {
      if (!this.activeEntity?.code || !this.cleanPreview?.can_apply) {
        this.cancelClean();
        return;
      }

      this.cleaningEntity = true;
      const entityCode = this.activeEntity.code;
      const entityName = this.activeEntity.name || entityCode;

      try {
        const result = await this.applyErpEntityClean({
          code: entityCode,
          payload: { confirm: true },
        });

        this.cancelClean();
        this.$refs.erpEntityGrid?.reload();
        this.writeSyncLog({
          entityCode,
          entityName,
          status: ERP_SYNC_LOG_STATUS.SUCCESS,
          stats: {
            deleted_count: result?.deleted_count || 0,
            mapping_links_nulled: result?.mapping_links_nulled || 0,
          },
          durationMs: result?.duration_ms || 0,
        });

        const message = this.$t("tools.erp_integration.clean_success", {
          count: result?.deleted_count || 0,
          entity: entityName,
        });
        notify(message, "success", 3000);
      } catch (e) {
        this.writeSyncLog({
          entityCode,
          entityName,
          status: ERP_SYNC_LOG_STATUS.ERROR,
          errorMessage: this.extractErrorMessage(e),
          durationMs: 0,
        });
        this.showActionError(e, "clean_error");
      } finally {
        this.cleaningEntity = false;
      }
    },
  },
  mounted() {
    this.bootstrap();
  },
  watch: {
    "$route.query": {
      handler(newQuery) {
        if (!newQuery) return;
        if (newQuery.entity && this.entities?.length) {
          const target = this.entities.find((e) => e.code === newQuery.entity);
          if (target && target.code !== this.activeCode) {
            this.setActiveEntity(target.code);
          }
        }
        if (newQuery.view && newQuery.view !== this.activeView) {
          this.setActiveView(newQuery.view);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/smagriaTabs.scss";

.nomenclatures-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.nomenclatures-header__title {
  margin: 0;
  color: #323232;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
}

.nomenclatures-back-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
}

.nomenclature-tabs,
.view-switcher {
  @include smagria-tab-list;
  flex-wrap: wrap;
}

.nomenclature-tab-btn,
.view-switcher__btn {
  @include smagria-tab-button;
}

.tab-content-container {
  position: relative;
  min-height: 180px;
}

.tab-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(1px);
}

.tab-loading-text {
  font-size: 12px;
  color: #4b5a70;
  font-weight: 600;
}

.tab-loading-spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #d7e4f3;
  border-top-color: #2e6db0;
  animation: erp-spin 0.8s linear infinite;
}

@keyframes erp-spin {
  to {
    transform: rotate(360deg);
  }
}

.view-switcher__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.view-panel {
  width: 100%;
}

.placeholder-message {
  border: 1px dashed #c7d3e4;
  border-radius: 10px;
  padding: 20px;
  color: #5c6a7b;
  background: #f8fbff;
}

.clean-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(28, 39, 56, 0.45);
  backdrop-filter: blur(2px);
}

.clean-confirm-dialog {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(20, 36, 60, 0.25);
  width: min(520px, 92vw);
  padding: 22px 24px;
}

.clean-confirm-title {
  margin: 0 0 12px 0;
  font-size: 18px;
  color: #2b3a4d;
}

.clean-confirm-summary {
  background: #f6f9ff;
  border: 1px solid #d7e4f3;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.clean-confirm-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
  color: #2b3a4d;
}

.clean-confirm-body {
  font-size: 14px;
  color: #4b5a70;
  line-height: 1.5;
  margin-bottom: 18px;
}

.clean-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
